import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  AUTH_TYPE as authorizationTypes,
  getSupplierMainAuthTypes,
} from 'feature/loading-management/utils/const';
import { arrayOf, func, number, oneOfType, shape, string } from 'prop-types';
import { isNotEmpty } from 'utils/helpers/formValidations';
import { GT_ZERO, REQUIRED } from 'utils/helpers/validationMessages';
import { useUserRolePermissions } from 'hooks';
import { FormHelperText } from '@mui/material';

const AuthorizationVolumes = ({ dataPath, validation, info, setValidation, setInfo }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  const dataLens = R.lensPath(dataPath);
  const authorizationTypeLens = R.o(dataLens, R.lensProp('authorizationType'));
  const authorizationVolumeLens = R.o(dataLens, R.lensProp('authorizationVolume'));
  const renewalVolumeLens = R.o(dataLens, R.lensProp('renewalVolume'));

  const authorizationTypeInfo = R.view(authorizationTypeLens, info);
  const authorizationVolume = R.view(authorizationVolumeLens, info);
  const renewalVolume = R.view(renewalVolumeLens, info);
  const [authorizationType, setAuthorizationType] = useState(null);

  useEffect(() => {
    if (R.not(R.isEmpty(authorizationTypeInfo))) {
      if (authorizationTypeInfo === 'ONEVOLDAILY') {
        setAuthorizationType('1VOLDAILY');
      } else if (authorizationTypeInfo === 'ONEVOLWEEKLY') {
        setAuthorizationType('1VOLWEEKLY');
      } else if (authorizationTypeInfo === 'ONEVOLMONTH') {
        setAuthorizationType('1VOLMONTH');
      } else {
        setAuthorizationType(authorizationTypeInfo);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAuthorizationType, authorizationTypeInfo]);

  // prettier-ignore
  // updateAuthorizationType :: authorizationType -> info -> info
  const updateAuthorizationType = R.curryN(2, R.compose(
      R.ifElse(
        R.propEq('authorizationType', 'ONELOAD'),
        R.assoc('authorizationVolume', 1),
        R.assoc('authorizationVolume', undefined)
      ),
      R.assoc('renewalVolume', undefined),
      R.assoc('authorizationType')
      ));

  // validateVolume :: Number -> String
  const validateVolume = R.cond([
    [R.equals(NaN), R.always(REQUIRED)],
    [R.gte(0), R.always(GT_ZERO)],
    [R.T, R.always('')],
  ]);

  const { isSupplier } = useUserRolePermissions();

  const originalFilteredTypes = types => types.filter(({ value }) => value !== 'ONEVOL');

  const filteredAuthTypes = isSupplier
    ? originalFilteredTypes(getSupplierMainAuthTypes())
    : originalFilteredTypes(authorizationTypes);

  // Check if the currently selected value is missing from the dropdown
  const isTypeMissing =
    isSupplier &&
    authorizationType &&
    !filteredAuthTypes.some(type => type.value === authorizationType);

  useEffect(() => {
    if (isTypeMissing && setValidation) {
      setValidation({
        ...validation,
        authorizationType: 'Required',
      });
    } else if (setValidation) {
      setValidation({ ...validation, authorizationType: '' });
    }
  }, [isTypeMissing, setValidation, validation]);

  const authVolCheck = ['ONELOAD', 'UNLIMITED', 'SPECIFIC'].includes(authorizationType);
  const renewVolCheck = ['LIMITED', 'ONELOAD', 'ONETIME', 'UNLIMITED', 'SPECIFIC'].includes(
    authorizationType
  );

  // initial info has to run through validation as endpoints
  // return values for these
  useEffect(() => {
    setValidation(R.assoc('authorizationVolume', ''));
    setValidation(R.assoc('renewalVolume', ''));
    if (R.and(R.not(R.isNil(authorizationType)), R.not(authVolCheck))) {
      R.compose(
        setValidation,
        R.assoc('authorizationVolume'),
        validateVolume,
        parseInt,
        R.prop('authorizationVolume')
      )(info);
    }
    if (R.and(R.not(R.isNil(authorizationType)), R.not(renewVolCheck))) {
      R.compose(
        setValidation,
        R.assoc('renewalVolume'),
        validateVolume,
        parseInt,
        R.prop('renewalVolume')
      )(info);
    }
    if (R.not(R.isNil(authorizationType)) && renewVolCheck && authVolCheck) {
      setValidation({
        ...validation,
        authorizationVolume: '',
        renewalVolume: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationType, authVolCheck, info, renewVolCheck]);

  // handleAuthorizationTypeChange :: AuthorizationType -> info
  const handleAuthorizationTypeChange = R.compose(
    setInfo,
    R.over(dataLens),
    updateAuthorizationType,
    R.path(['target', 'value'])
  );

  // handleAuthorizationVolumeChange :: event -> info
  const handleAuthorizationVolumeChange = R.compose(
    setInfo,
    R.over(dataLens),
    R.assoc('authorizationVolume'),
    R.tap(R.compose(setValidation, R.assoc('authorizationVolume'), validateVolume)),
    parseInt,
    R.path(['target', 'value'])
  );

  // handleRenewalVolumeChange :: event -> info
  const handleRenewalVolumeChange = R.compose(
    setInfo,
    R.over(dataLens),
    R.assoc('renewalVolume'),
    R.tap(R.compose(setValidation, R.assoc('renewalVolume'), validateVolume)),
    parseInt,
    R.path(['target', 'value'])
  );

  return (
    <Stack direction={lgUp ? 'row' : 'column'} spacing={2}>
      <FormControl
        error={!!validation?.authorizationType && isTypeMissing}
        required
        sx={{ flexBasis: 0, flexGrow: 1 }}
      >
        <InputLabel id="authorization-type">Authorization Type</InputLabel>
        <Select
          label="Authorization Type"
          labelId="authorization-type"
          onChange={handleAuthorizationTypeChange}
          value={R.isNil(authorizationType) ? 'UNLIMITED' : authorizationType}
        >
          {filteredAuthTypes.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
        {isTypeMissing && <FormHelperText>Required</FormHelperText>}
      </FormControl>

      <TextField
        disabled={authVolCheck}
        error={isNotEmpty(validation.authorizationVolume)}
        helperText={R.prop('authorizationVolume', validation) || 'Volume (gal)'}
        label="Authorization"
        onChange={handleAuthorizationVolumeChange}
        placeholder="--"
        required={R.not(authVolCheck)}
        slotProps={{
          inputLabel: { shrink: true },
        }}
        sx={{ flexBasis: 0, flexGrow: 1 }}
        value={R.defaultTo('', authorizationVolume)}
      />

      <TextField
        disabled={renewVolCheck}
        error={isNotEmpty(validation.renewalVolume)}
        helperText={R.prop('renewalVolume', validation) || 'Volume (gal)'}
        label="Renewal"
        onChange={handleRenewalVolumeChange}
        placeholder="--"
        required={R.not(renewVolCheck)}
        slotProps={{
          inputLabel: { shrink: true },
        }}
        sx={{ flexBasis: 0, flexGrow: 1 }}
        value={R.defaultTo('', renewalVolume)}
      />
    </Stack>
  );
};

AuthorizationVolumes.propTypes = {
  dataPath: arrayOf(oneOfType([number, string])).isRequired,
  info: shape({}),
  setInfo: func.isRequired,
  setValidation: func,
  validation: shape({}),
};

AuthorizationVolumes.defaultProps = {
  info: {},
  setValidation: R.always(),
  validation: {},
};

export default AuthorizationVolumes;
